import CardHeader from "components/CardHeader";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { Asset } from "models/Asset";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Card, CardBody, Spinner } from "reactstrap";
import * as api from "services/Api";
import ComputerRow from "../../Customer/ComputerRow";
import { assetTypeIcon } from "../../Assets/assetPresentation";

interface Props {
    accountId: string;
    tenantId: string;
}

const AccountAssetsCard: React.FC<Props> = ({ accountId, tenantId }) => {
    const { t } = useOfficeTranslation();
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        api.getAssets({ customerId: accountId })
            .then(values => { if (active) setAssets(values); })
            .catch(() => { if (active) setError(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [accountId]);

    const dueCount = useMemo(
        () => assets.flatMap(asset => asset.service_intervals).filter(interval => interval.due).length,
        [assets]
    );
    const overdueCount = useMemo(
        () => assets.flatMap(asset => asset.service_intervals).filter(interval => interval.overdue).length,
        [assets]
    );

    return <Card>
        <CardBody>
            <CardHeader
                title={t("assets.title")}
                icon="mdi-devices"
                count={assets.length}
                actions={<Button
                    color="primary"
                    outline
                    size="sm"
                    tag={Link}
                    to={`/assets/add?customer_id=${encodeURIComponent(accountId)}&tenant_id=${encodeURIComponent(tenantId)}`}
                >
                    <i className="mdi mdi-plus me-1" />{t("assets.newAsset")}
                </Button>}
            />

            {error && <Alert color="danger" className="mt-3 mb-0">{t("assets.relation.loadFailed")}</Alert>}
            {loading && <div className="text-center py-3"><Spinner size="sm" /></div>}

            {!loading && !error && (dueCount > 0 || overdueCount > 0) && <div className="d-flex gap-2 mt-3">
                {dueCount > 0 && <Badge color="warning">{t("assets.relation.dueCount", { count: dueCount })}</Badge>}
                {overdueCount > 0 && <Badge color="danger">{t("assets.relation.overdueCount", { count: overdueCount })}</Badge>}
            </div>}

            {!loading && assets.map(asset => <div key={asset.id}>
                <div className={`py-3${asset.managers.some(manager => manager.enabled) ? "" : " border-bottom"}`}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="avatar-sm flex-shrink-0"><span className="avatar-title rounded bg-primary bg-soft text-primary font-size-20"><i className={`mdi ${assetTypeIcon(asset.type)}`} /></span></span>
                            <div>
                                <Link to={`/assets/${asset.id}`} className="fw-semibold">{asset.name}</Link>
                                <div className="small text-muted text-capitalize">
                                    {asset.type.replaceAll("_", " ")} · {asset.status}
                                </div>
                            </div>
                        </div>
                        <Badge color={asset.criticality === "critical" ? "danger" : asset.criticality === "high" ? "warning" : "secondary"}>
                            {asset.criticality}
                        </Badge>
                    </div>
                </div>
                <div className="ms-3">
                    {asset.managers.filter(manager => manager.enabled).map(manager => <ComputerRow key={manager.id} asset={asset} manager={manager} />)}
                </div>
            </div>)}

            {!loading && !assets.length && !error &&
                <p className="text-muted mt-3 mb-0">{t("assets.relation.none")}</p>}
        </CardBody>
    </Card>;
};

export default AccountAssetsCard;
